import { Navigate, useNavigate } from 'react-router-dom';
import { apiClient } from '@devd-client/api';
import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Img,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  GoogleIcon,
  Loader,
  MicrosoftIcon,
  PasswordInput,
  useToastHook,
} from '@devd-client/devd/components';
import {
  GoogleAuthProvider,
  OAuthProvider,
  getRedirectResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';
import { auth } from '../../helpers/auth';
import { useAuth } from '../../context/devd-auth';
import { FIREBASE_ERRORS } from '../../helpers/firebaseErrors';
import { useSearchParams } from 'react-router-dom';
import { FormLink } from './register.styles';
import { BreakItem, BreakItemContent } from '../login/login.styled';

const initialValues = {
  email: '',
  orgId: '',
  password: '',
};

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(12, 'Password must be at least 12 characters.')
    .max(128, 'Password must be lower than 128 characters.')
    .matches(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])(?!.*?\b(?:password|admin|user)\b).*$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.'
    )
    .required('Password is required.'),
});

export function Register() {
  const [isLoading, setIsLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [newToast] = useToastHook();

  const [data, setData] = useState<any>('');
  const [message, setMessage] = useState<any>('');
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [orgId, setOrgId] = useState<string>('');

  const [searchParams] = useSearchParams();
  const token = searchParams.get('code');
  const urlOrgId = searchParams.get('orgId');

  const [appState] = useAuth();
  const navigate = useNavigate();

  const handleLogin = (email: string, password: string) => {
    setIsLoading(true);

    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        setIsLoading(false);
        setRedirect(true);
      })
      .catch((error) => {
        setIsLoading(false);
        newToast({
          message:
            FIREBASE_ERRORS[error?.message as keyof typeof FIREBASE_ERRORS] ||
            error?.message,
          status: 'error',
        });
      });
  };

  const regenerateSignupToken = async () => {
    try {
      setIsLoading(true);
      if (!orgId) {
        setIsLoading(false);
        newToast({
          message: 'Organization ID is missing. Cannot regenerate token.',
          status: 'error',
        });
        return;
      }
      const res = await apiClient('/v1/org/regenerate-signup-token', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orgId }),
      });

      setIsLoading(false);
      if (res) {
        newToast({
          message: 'New verification link has been sent to your email.',
          status: 'success',
        });
      }
    } catch (err: any) {
      setIsLoading(false);
      newToast({
        message: err.message ?? 'Failed to regenerate token. Please try again.',
        status: 'error',
      });
    }
  };

  useEffect(() => {
    if (urlOrgId) {
      setOrgId(urlOrgId);
    }

    const verifySignupToken = async () => {
      try {
        setIsLoading(true);
        const res = await apiClient('/v1/org/verify-signup-token', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, orgId: urlOrgId }),
        });

        setIsLoading(false);
        if (res) {
          setData(res);
        }
      } catch (err: any) {
        setIsLoading(false);
        if (err.message === 'Invalid or expired token.') {
          setIsTokenExpired(true);
          setMessage('Token is expired. Please regenerate the link.');
        } else if (err.code === '302 FOUND') {
          navigate('/account/login');
        } else {
          setMessage(err.message ?? 'Error verifying signup token');
        }
        setMessage(err.message ?? 'Error verifying signup token');
      }
    };

    verifySignupToken();
  }, [token, urlOrgId]);

  const OnSignUp = (email: any, password: any, orgId: any) => {
    try {
      setIsLoading(true);
      apiClient(`/v1/org/${orgId}/root-user`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailId: email,
          orgCode: orgId,
          password: password,
        }),
      })
        .then((res: any) => {
          setIsLoading(false);
          newToast({
            message: 'Signup successful.',
            status: 'success',
          });
          setTimeout(() => {
            handleLogin(email, password);
          }, 1000);
        })
        .catch((err) => {
          setIsLoading(false);
          newToast({
            message:
              err.message ??
              'Set password failed. Contact DevDynamics support.',
            status: 'error',
          });
        });
    } catch (err) {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginWithOutRootUserCall = () => {
    const provider = new GoogleAuthProvider();
    signInWithRedirect(auth, provider)
      .then(() => {
        setIsLoading(false);
        newToast({
          message: `Login successful`,
          status: 'success',
        });
        setRedirect(true);
      })
      .catch((err) => {
        setIsLoading(false);
        newToast({
          message: `${err.message}`,
          status: 'error',
        });
      });
  };

  const handleRegistrationError = (err: Error) => {
    setIsLoading(false);
    auth.signOut();
    setTimeout(() => {
      window.location.href = '/account/login';
    }, 500);
    newToast({
      message: err?.message ?? 'Signup Failed. Contact DevDynamics support.',
      status: 'error',
    });
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();

    signInWithPopup(auth, provider)
      .then((result) => {
        apiClient(`/v1/org/${data?.dto?.orgId}/root-user`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            emailId: data?.dto?.email,
            orgCode: data?.dto?.orgId,
            authId: result?.user?.uid,
          }),
        })
          .then((res: any) => {
            setIsLoading(false);
            newToast({
              message: 'Signup successful.',
              status: 'success',
            });
            auth?.currentUser?.getIdToken(true);
            setTimeout(() => {
              navigate('/account/login');
            }, 1000);
          })
          .catch(handleRegistrationError);
      })
      .catch((err) => {
        newToast({
          message: `${err.message}`,
          status: 'error',
        });
      });
  };

  const handleMicrosoftLogin = async () => {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({ tenant: 'common' });

    signInWithPopup(auth, provider)
      .then((result) => {
        apiClient(`/v1/org/${data?.dto?.orgId}/root-user`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            emailId: data?.dto?.email,
            orgCode: data?.dto?.orgId,
            authId: result?.user?.uid,
          }),
        })
          .then((res: any) => {
            setIsLoading(false);
            newToast({
              message: 'Signup successful.',
              status: 'success',
            });
            auth?.currentUser?.getIdToken(true);
            setTimeout(() => {
              navigate('/account/login');
            }, 1000);
          })
          .catch(handleRegistrationError);
      })
      .catch((err) => {
        newToast({
          message: `${err.message}`,
          status: 'error',
        });
      });
  };

  if (isTokenExpired) {
    return (
      <Box>
        <Flex align="center" justify="center" mb={4} mt={4}>
          <Text fontFamily="heading" fontSize="lg" color="text.primary">
            Token Expired
          </Text>
        </Flex>

        <Text
          textAlign={'center'}
          fontFamily="heading"
          fontSize="sm"
          color="text.secondary"
          mb={6}
        >
          Your verification link has expired. Please regenerate a new link.
        </Text>

        <Button
          variant="tertiary"
          w="full"
          fontSize="sm"
          onClick={regenerateSignupToken}
          isLoading={isLoading}
        >
          Regenerate Link
        </Button>

        <Box mt={4}>
          <Text
            fontFamily="heading"
            fontSize="md"
            fontWeight="normal"
            textAlign={'center'}
            color="text.primary"
          >
            Already have an account?
            <FormLink to={'/account/login'}> Login</FormLink>
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {appState.user && <Navigate to="/" />}
      {redirect === true && !appState.user && <Loader />}

      <Formik
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          OnSignUp(
            (values.email = data?.dto?.email),
            values.password,
            (values.orgId = data?.dto?.orgId)
          );
          setSubmitting(false);
        }}
        validationSchema={validationSchema}
      >
        {(props) => {
          const { isSubmitting, handleSubmit } = props;
          return (
            <form onSubmit={handleSubmit as any}>
              <Flex align="center" justify="center">
                <Text fontFamily="heading" fontSize="sm" color="text.primary">
                  Hi
                </Text>
                <Text
                  ml={2}
                  as="span"
                  color="text.primary"
                  fontWeight="semibold"
                  fontFamily="heading"
                >
                  {data && data?.dto?.firstName}{' '}
                </Text>
              </Flex>

              <Text
                textAlign={'center'}
                fontFamily="heading"
                fontSize="sm"
                color="text.primary"
                mb={4}
              >
                Please set account password to finish your registration.
              </Text>

              {message && (
                <Alert status="error" mb={2}>
                  <AlertIcon />
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}

              <Stack spacing={4}>
                <PasswordInput
                  name="password"
                  label="Password"
                  placeholder="Password"
                />

                <Button
                  isDisabled={isSubmitting || !data?.dto?.email}
                  variant="tertiary"
                  w="full"
                  fontSize="sm"
                  type="submit"
                  onClick={() => {
                    window.analytics.track('user_auth', {
                      status: 'init',
                    });
                  }}
                >
                  Sign Up
                </Button>

                <BreakItem mt={20} mb={20}>
                  <BreakItemContent>OR</BreakItemContent>
                </BreakItem>

                <Flex gap={4}>
                  <Button
                    color={'text.primary'}
                    isDisabled={isSubmitting}
                    spinnerPlacement="start"
                    variant={'secondary'}
                    w="full"
                    mb={5}
                    onClick={handleGoogleLogin}
                  >
                    <GoogleIcon height="18px" width="18px" />
                    &nbsp; Signup With Google
                  </Button>

                  <Button
                    color={'text.primary'}
                    isDisabled={isSubmitting}
                    spinnerPlacement="start"
                    variant={'secondary'}
                    w="full"
                    mb={5}
                    onClick={handleMicrosoftLogin}
                  >
                    <MicrosoftIcon height="18px" width="18px" />
                    &nbsp; Signup With Microsoft
                  </Button>
                </Flex>

                <Box>
                  <Text
                    fontFamily="heading"
                    fontSize="md"
                    fontWeight="normal"
                    textAlign={'center'}
                    color="text.primary"
                    mt={4}
                  >
                    Already have an account?
                    <FormLink to={'/account/login'}> Login</FormLink>
                  </Text>

                  <Text
                    align={'center'}
                    fontSize={'xs'}
                    fontFamily="heading"
                    color="text.secondary"
                    mt={'12px'}
                  >
                    By continuing, you agree to our{' '}
                    <Link
                      href={'https://devdynamics.ai/terms'}
                      target="_blank"
                      color="primary"
                    >
                      Terms{' '}
                    </Link>{' '}
                    of Service and{' '}
                    <Link
                      href={'https://devdynamics.ai/privacy'}
                      target="_blank"
                      color="primary"
                    >
                      Privacy{' '}
                    </Link>{' '}
                    Policy.
                  </Text>
                </Box>
              </Stack>
            </form>
          );
        }}
      </Formik>

      {isLoading && <Loader />}
    </>
  );
}

export default Register;
